import { useCallback, useState } from 'react'

import { Alert } from '@vtex/shoreline'

import {
  Drawer,
  ToggleButton,
  useColumnVisibility
} from '~/components/ColumnSelector'
import HeaderPages from '~/components/HeaderPages'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import LoadingPopUp from '~/components/LoadingPopUp'
import Panel from '~/components/Panel'
import Table from '~/components/Table'
import { useAlert } from '~/hooks/useAlert'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import ExportButton from '~/modules/retailMedia/components/ExportButton'
import LookbackSelector from '~/modules/retailMedia/components/LookbackSelector'
import { hasVisibleNtbColumn } from '~/modules/retailMedia/helpers/newMetricsAvailability'
import { Footer } from '~/pages/products/list/styles'

import CampaignListFilters from '../CampaignFilters'
import { useCampaignTable } from './hooks/useCampaignTable'
import { schema as buildCampaignSchema } from './schemas'

type CampaignTableSchema = ReturnType<typeof buildCampaignSchema>

const CAMPAIGN_TABLE_READONLY_KEYS = [
  'campaingInfo'
] as const satisfies ReadonlyArray<keyof CampaignTableSchema>

const CampaignTable = () => {
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)
  const {
    data,
    error,
    isValidating,
    innerLoading,
    page,
    perPage,
    total,
    handlePagination,
    handleItemsPerPageChange,
    sortDirection,
    sortKey,
    handleSortList,
    schema,
    queryParams,
    lookbackMonths,
    handleLookbackChange
  } = useCampaignTable()

  const { t } = useAPPTranslation()

  const [AlertOnError] = useAlert(error)
  const handleOpenColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(true)
  }, [])

  const handleCloseColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(false)
  }, [])

  const { columns, visibleColumnsSchema, onColumnsChange, onResetToDefault } =
    useColumnVisibility({
      schema,
      tableName: 'campaigns',
      readonlyKeys: CAMPAIGN_TABLE_READONLY_KEYS
    })

  // seletor de lookback só aparece quando há coluna NTB visível/selecionada
  const hasNtbMetrics = !!hasVisibleNtbColumn(visibleColumnsSchema)

  return (
    <>
      <HeaderPages
        title={t('rm:listingCampaigns')}
        sideRightComponent={
          <>
            {hasNtbMetrics && (
              <LookbackSelector
                value={lookbackMonths}
                onChange={handleLookbackChange}
              />
            )}
            <ToggleButton onClick={handleOpenColumnSelector} />
            <ExportButton queryParams={queryParams} exportType={'campaigns'} />
          </>
        }
      />
      <Drawer
        columns={columns}
        isOpen={isColumnSelectorOpen}
        onClose={handleCloseColumnSelector}
        onColumnsChange={onColumnsChange}
        onResetToDefault={onResetToDefault}
      />

      <CampaignListFilters />

      <Loading status={!data && isValidating}>Carregando campanhas...</Loading>

      <LoadingPopUp isActive={isValidating} />

      <AlertOnError />

      <Panel noPaddingBody isLoading={!data}>
        <Loading status={innerLoading} onlyLine />

        {!!data && (
          <Table
            schema={visibleColumnsSchema}
            data={data}
            total={total}
            sideBorder
            smallerRowPadding
            activeSortDirection={sortDirection}
            activeSortKey={sortKey}
            onSort={handleSortList}
            freeze="first-row-and-column"
          />
        )}

        {total === 0 && data && (
          <Alert variant="warning">
            {t('rm:metrics.campaigns.notFoundWithFilters')}
          </Alert>
        )}
      </Panel>

      <Footer>
        <ListPagination
          total={total}
          label={total > 1 ? t('rm:Campaigns') : t('rm:Campaign')}
          currentPage={page}
          itemsPerPage={perPage}
          menuPlacement="top"
          onClickPagination={handlePagination}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Footer>
    </>
  )
}

export default CampaignTable
